/* Canvas Pickleball — Custom Pickleball Designer
 * 3D ball with REAL see-through holes (alphaTest + instanced wall tubes for moulded
 * thickness + inner cavity sphere), bump-map hole bevels + orange peel, glossy
 * plastic (clearcoat), plus a 2D "Placement" view where the logo & text drag freely.
 * Logo/text positions live in ball-radius units (x,y ∈ [-.72,.72]) and map onto the
 * equirect texture, so 2D placement and the 3D print always agree.
 * Quote flow: {product:'pickleball', summary, json, proof} → cpd_quote_v1 → /pages/custom-design. */
const CFG = window.CPB_CONFIG || {};
const el = id => document.getElementById(id);
const LS_KEY = 'cpb_design_v1';

const BALL_COLORS = [
  ['Neon Green', '#9be424'], ['Yellow', '#ffd93b'], ['Orange', '#ff8b3d'], ['White', '#f2f3f4'],
  ['Pink', '#ff5da2'], ['Red', '#e5484d'], ['Blue', '#379fe8'], ['Green', '#3ecf6e'],
  ['Purple', '#8b5cf6'], ['Black', '#1f2227'],
];
const state = { color:'#9be424', holes:40, logoSrc:'', logo:null, logoScale:1, removeWhite:false,
  logoX:0, logoY:-0.22, logoRot:0, logoFlipX:false, logoFlipY:false,
  text:'', textFont:'sans', textColor:'#111111', textSize:1, textX:0, textY:0.60, textRot:0,
  notes:'', view:'3d' };

/* web-safe font menu — every stack resolves on iOS/Android/desktop without loading webfonts */
const FONTS = [
  ['sans',   'Bold Sans',  '800', "'Helvetica Neue', Arial, sans-serif"],
  ['athletic','Athletic',  '400', "Impact, 'Arial Black', 'Franklin Gothic Bold', sans-serif"],
  ['serif',  'Serif',      '700', "Georgia, 'Times New Roman', serif"],
  ['script', 'Script',     '400', "'Brush Script MT', 'Snell Roundhand', 'Segoe Script', cursive"],
  ['rounded','Rounded',    '800', "'Arial Rounded MT Bold', 'Trebuchet MS', sans-serif"],
  ['mono',   'Typewriter', '700', "'Courier New', Courier, monospace"],
];
const fontOf  = k => FONTS.find(f => f[0] === k) || FONTS[0];
const fontCSS = (k, px) => { const f = fontOf(k); return `${f[2]} ${px}px ${f[3]}`; };

const lum = h => { const n = parseInt(h.slice(1), 16); return (0.2126*(n>>16 & 255) + 0.7152*(n>>8 & 255) + 0.0722*(n & 255)) / 255; };
const shade = (hex, f) => { const n = parseInt(hex.slice(1), 16);
  const ch = i => Math.max(0, Math.min(255, Math.round(((n>>i & 255)) * (1+f))));
  return '#' + [16,8,0].map(i => ch(i).toString(16).padStart(2,'0')).join(''); };
const colorName = h => (BALL_COLORS.find(c => c[1].toLowerCase() === h.toLowerCase()) || ['Custom'])[0];
const fileToDataURL = f => new Promise(r => { const fr = new FileReader(); fr.onload = () => r(fr.result); fr.readAsDataURL(f); });
const loadImg = src => new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });
function downscale(img, max){ const s = Math.min(1, max/Math.max(img.naturalWidth, img.naturalHeight)); if (s >= 1) return img.src;
  const c = document.createElement('canvas'); c.width = Math.round(img.naturalWidth*s); c.height = Math.round(img.naturalHeight*s);
  c.getContext('2d').drawImage(img, 0, 0, c.width, c.height); return c.toDataURL('image/png'); }
function removeWhiteBg(img){ const c = document.createElement('canvas'); c.width = img.naturalWidth; c.height = img.naturalHeight;
  const x = c.getContext('2d'); x.drawImage(img, 0, 0); const d = x.getImageData(0, 0, c.width, c.height), p = d.data;
  for (let i = 0; i < p.length; i += 4){ if (p[i] > 236 && p[i+1] > 236 && p[i+2] > 236) p[i+3] = 0; }
  x.putImageData(d, 0, 0); return c.toDataURL('image/png'); }

/* ---- hole layouts: geodesically EVEN spreads (every neighbour gap near-equal) ----
 * Outdoor 40: poles + 7 @ ±48° + 12 @ ±15° — gaps ≈ 29–35° all over, like a real X-40.
 * Indoor 26: rhombicuboctahedron vertices (8 @ ±21°, 4 @ ±59.6°) + poles — gaps ≈ 42–45°.
 * Band format: [latitude°, count, lon-offset°]. */
function holePoints(n){
  const bands = n > 30
    ? [[75, 4, 0], [45, 8, 0], [15, 8, 0], [-15, 8, 0], [-45, 8, 0], [-75, 4, 0]]                // outdoor 40: aligned rows & columns (no stagger)
    : [[90, 1, 0], [59.6, 4, 45], [21, 8, 22.5], [-21, 8, 22.5], [-59.6, 4, 45], [-90, 1, 0]];   // indoor 26
  const pts = [];
  bands.forEach(b => { const [deg, k, offDeg] = b, lat = deg * Math.PI / 180, off = offDeg * Math.PI / 180;
    for (let i = 0; i < k; i++) pts.push({ lat, lon: ((i / k) * 2 * Math.PI + off) % (2*Math.PI) }); });
  return pts;
}
const HOLE_ANG = () => state.holes > 30 ? 0.095 : 0.125;   // hole angular radius (rad): outdoor smaller, indoor bigger (matched to product photos)

/* print positions (ball-radius units) → texture px */
const TW = 2048, TH = 1024;
const posToTex = (px, py) => { const lon = px * 0.8, lat = -py * 0.8;   // py is screen-down, lat is north-up
  return { u: (0.5 + lon / (2*Math.PI)) * TW, v: (0.5 - lat / Math.PI) * TH, stretch: 1 / Math.cos(lat) }; };

/* ---- colour map + alpha map (holes cut clean through via alphaTest) ---- */
function ballCanvases(){
  const c = document.createElement('canvas'); c.width = TW; c.height = TH; const x = c.getContext('2d');
  const a = document.createElement('canvas'); a.width = TW; a.height = TH; const ax = a.getContext('2d');
  x.fillStyle = state.color; x.fillRect(0, 0, TW, TH);
  ax.fillStyle = '#fff'; ax.fillRect(0, 0, TW, TH);
  /* soft blow-moulded mottle: large feathered blotches, not dots (real balls have faint density swirls) */
  { let seed = 13; const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
    for (let i = 0; i < 130; i++){ const rx = rnd()*TW, ry = TH*(0.1 + 0.8*rnd()), rr = 40 + rnd()*110;
      const tone = rnd() < 0.5 ? '0,0,0' : '255,255,255';
      const g = x.createRadialGradient(rx, ry, 0, rx, ry, rr);
      g.addColorStop(0, `rgba(${tone},${(0.018 + rnd()*0.022).toFixed(3)})`); g.addColorStop(1, `rgba(${tone},0)`);
      x.fillStyle = g; x.beginPath(); x.arc(rx, ry, rr, 0, Math.PI*2); x.fill(); } }
  /* moulding seam: barely-there hairline like the real ball (the ridge itself lives in the bump map) */
  x.fillStyle = 'rgba(0,0,0,.07)'; x.fillRect(0, TH/2 - 1.2, TW, 2.4);
  x.fillStyle = 'rgba(255,255,255,.08)'; x.fillRect(0, TH/2 + 1.2, TW, 1.2);
  /* printed logo + text FIRST (hole rims + alphaTest cutouts then read as printed-over) */
  if (state.logo){
    const im = state.logo, p = posToTex(state.logoX, state.logoY);
    const maxW = TW * 0.15 * state.logoScale * p.stretch, maxH = TH * 0.3 * state.logoScale;
    const s = Math.min(maxW / im.naturalWidth, maxH / im.naturalHeight);
    x.save(); x.translate(p.u, p.v); x.rotate(state.logoRot * Math.PI / 180);
    x.scale(state.logoFlipX ? -1 : 1, state.logoFlipY ? -1 : 1);
    x.drawImage(im, -im.naturalWidth*s/2, -im.naturalHeight*s/2, im.naturalWidth*s, im.naturalHeight*s);
    x.restore();
  }
  if (state.text){
    const p = posToTex(state.textX, state.textY), fs = Math.round(72 * state.textSize);
    x.save(); x.translate(p.u, p.v); x.rotate(state.textRot * Math.PI / 180);
    x.fillStyle = state.textColor || (lum(state.color) < 0.5 ? '#fff' : '#111');
    x.font = fontCSS(state.textFont, fs); x.textAlign = 'center'; x.textBaseline = 'middle';
    x.fillText(state.text.slice(0, 24), 0, 0);   // straight in the texture = curved on the ball
    x.restore();
  }
  /* holes: transparent core (alpha map) + a soft AO ring in the colour map.
   * NO painted rims/lips — the rounded edge is lit for real by the bump-map bevel + wall tubes. */
  const angR = HOLE_ANG();
  for (const h of holePoints(state.holes)){
    const u0 = (h.lon / (2*Math.PI)) * TW, v0 = (0.5 - h.lat / Math.PI) * TH;
    const ry = (angR / Math.PI) * TH, rx = ry / Math.max(0.2, Math.cos(h.lat));
    if (Math.cos(h.lat) < 0.15){                              // polar cap: full-width band (ellipse math breaks at the poles)
      const top = h.lat > 0;
      const g = x.createLinearGradient(0, top ? ry : TH - ry, 0, top ? ry*1.6 : TH - ry*1.6);
      g.addColorStop(0, 'rgba(0,0,0,.2)'); g.addColorStop(1, 'rgba(0,0,0,0)');
      x.fillStyle = g; x.fillRect(0, top ? ry : TH - ry*1.6, TW, ry*0.6);
      ax.fillStyle = '#000'; ax.fillRect(0, top ? 0 : TH - ry, TW, ry);
      continue;
    }
    for (const u of [u0, u0 + TW, u0 - TW]){
      if (u < -rx*2 || u > TW + rx*2) continue;
      x.save(); x.translate(u, v0); x.scale(rx/ry, 1);        // scaled circle = elliptical gradient
      const ao = x.createRadialGradient(0, 0, ry*0.8, 0, 0, ry*1.6);
      ao.addColorStop(0, 'rgba(0,0,0,.2)'); ao.addColorStop(0.3, 'rgba(0,0,0,.09)'); ao.addColorStop(1, 'rgba(0,0,0,0)');
      x.fillStyle = ao; x.beginPath(); x.arc(0, 0, ry*1.6, 0, Math.PI*2); x.fill();
      x.restore();
      ax.save(); ax.translate(u, v0); ax.fillStyle = '#000';
      ax.beginPath(); ax.ellipse(0, 0, rx, ry, 0, 0, Math.PI*2); ax.fill(); ax.restore();  // cut at full size — the lathe lip owns the edge
    }
  }
  return { map: c, alpha: a };
}

/* ---- equirect bump map: orange-peel undulation + rounded hole bevels + seam ridge ----
 * Cached per hole-count (colour never changes surface relief). This is what makes hole
 * edges catch light like moulded plastic instead of looking like painted rings. */
let _noiseCv = null;
function noiseCanvas(){ if (_noiseCv) return _noiseCv;
  const c = document.createElement('canvas'); c.width = c.height = 512; const nx = c.getContext('2d');
  const id = nx.createImageData(512, 512), d = id.data;
  let s = 7; const rnd = () => (s = (s * 16807) % 2147483647) / 2147483647;
  for (let i = 0; i < d.length; i += 4){ const v = 165 + rnd()*60; d[i] = d[i+1] = d[i+2] = v; d[i+3] = 255; }
  nx.putImageData(id, 0, 0); _noiseCv = c; return c; }
const _bumpCache = {};
function bumpCanvas(){
  if (_bumpCache[state.holes]) return _bumpCache[state.holes];
  const c = document.createElement('canvas'); c.width = TW; c.height = TH; const x = c.getContext('2d');
  x.fillStyle = '#808080'; x.fillRect(0, 0, TW, TH);
  const n = noiseCanvas();
  x.globalAlpha = 0.28;                                    // fine matte stipple like the real ball's skin
  for (let i = 0; i < TW; i += 256) for (let j = 0; j < TH; j += 256) x.drawImage(n, i, j, 256, 256);
  x.globalAlpha = 0.11; x.imageSmoothingEnabled = true;    // broader, softer undulation
  for (let i = 0; i < TW; i += 1024) for (let j = -256; j < TH; j += 1024) x.drawImage(n, i, j, 1024, 1024);
  x.globalAlpha = 1;
  /* seam ridge (subtle raised line + faint grooves either side) */
  x.fillStyle = 'rgba(255,255,255,.3)'; x.fillRect(0, TH/2 - 2, TW, 4);
  x.fillStyle = 'rgba(0,0,0,.2)'; x.fillRect(0, TH/2 - 4, TW, 1.6); x.fillRect(0, TH/2 + 2.4, TW, 1.6);
  /* each hole: surface dips toward the cut edge = rounded moulded bevel */
  const angR = HOLE_ANG();
  for (const h of holePoints(state.holes)){
    const u0 = (h.lon / (2*Math.PI)) * TW, v0 = (0.5 - h.lat / Math.PI) * TH;
    const ry = (angR / Math.PI) * TH, rx = ry / Math.max(0.2, Math.cos(h.lat));
    if (Math.cos(h.lat) < 0.15){                              // polar cap: band dip instead of ellipse
      const top = h.lat > 0;
      const g = x.createLinearGradient(0, top ? ry*0.85 : TH - ry*0.85, 0, top ? ry*1.45 : TH - ry*1.45);
      g.addColorStop(0, 'rgba(0,0,0,.5)'); g.addColorStop(0.35, 'rgba(0,0,0,.22)'); g.addColorStop(1, 'rgba(0,0,0,0)');
      x.fillStyle = g; x.fillRect(0, top ? ry*0.85 : TH - ry*1.45, TW, ry*0.6);
      continue;
    }
    for (const u of [u0, u0 + TW, u0 - TW]){
      if (u < -rx*2 || u > TW + rx*2) continue;
      x.save(); x.translate(u, v0); x.scale(rx/ry, 1);
      const g = x.createRadialGradient(0, 0, ry*0.85, 0, 0, ry*1.45);
      g.addColorStop(0, 'rgba(0,0,0,.5)'); g.addColorStop(0.35, 'rgba(0,0,0,.22)'); g.addColorStop(1, 'rgba(0,0,0,0)');
      x.fillStyle = g; x.beginPath(); x.arc(0, 0, ry*1.45, 0, Math.PI*2); x.fill();
      x.restore();
    }
  }
  _bumpCache[state.holes] = c; return c;
}

/* ---------------- 3D ball ---------------- */
let ball = null, place = null;
class Ball3D {
  constructor(THREE, OrbitControls, host, RoomEnvironment){
    this.THREE = THREE; this.host = host;
    const W = host.clientWidth || 500, H = host.clientHeight || 500;
    this.scene = new THREE.Scene();
    { const bg = document.createElement('canvas'); bg.width = 4; bg.height = 256; const bx = bg.getContext('2d');
      const g = bx.createLinearGradient(0, 0, 0, 256); g.addColorStop(0, '#ffffff'); g.addColorStop(0.55, '#f7f9fa'); g.addColorStop(1, '#eceff1');
      bx.fillStyle = g; bx.fillRect(0, 0, 4, 256);
      const bt = new THREE.CanvasTexture(bg); bt.colorSpace = THREE.SRGBColorSpace; this.scene.background = bt; }
    this.camera = new THREE.PerspectiveCamera(30, W/H, 0.1, 50); this.camera.position.set(0, 0, 5.4);
    this.renderer = new THREE.WebGLRenderer({ antialias:true, preserveDrawingBuffer:true });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2)); this.renderer.setSize(W, H);
    if ('outputColorSpace' in this.renderer) this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping; this.renderer.toneMappingExposure = 1.15;
    host.appendChild(this.renderer.domElement);
    this.canvas = this.renderer.domElement;
    if (RoomEnvironment){ try { const pm = new THREE.PMREMGenerator(this.renderer);
      this.scene.environment = pm.fromScene(new RoomEnvironment(), 0.04).texture; } catch(e){} }
    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.enableDamping = true; this.controls.enablePan = false;
    this.controls.minDistance = 3.0; this.controls.maxDistance = 7;
    this.controls.autoRotate = true; this.controls.autoRotateSpeed = 1.4;
    this.controls.addEventListener('start', () => { this.controls.autoRotate = false; });
    if (matchMedia('(pointer:coarse)').matches) this.canvas.style.touchAction = 'pan-y';
    /* env-dominant lighting reads photographic; analytic lights only shape, never carry.
     * The fill from below-front is the white-sweep bounce every product photo has. */
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x92969c, 1.0));
    const key = new THREE.DirectionalLight(0xfff6ec, 0.72); key.position.set(1.4, 1.8, 1.6); this.scene.add(key);
    const rim = new THREE.DirectionalLight(0xe8eeff, 0.35); rim.position.set(-1.6, 0.4, -1.2); this.scene.add(rim);
    const fill = new THREE.DirectionalLight(0xffffff, 0.55); fill.position.set(0.3, -1, 1.5); this.scene.add(fill);
    /* roughness variation only (relief now comes from the equirect bump map) */
    this.roughTex = new THREE.CanvasTexture(noiseCanvas());
    this.roughTex.wrapS = this.roughTex.wrapT = THREE.RepeatWrapping; this.roughTex.repeat.set(6, 3);
    /* glossy shell with holes punched through + a dark cavity you can see into */
    const { map, alpha } = ballCanvases();
    this.mat = new THREE.MeshPhysicalMaterial({ map:this._tex(map), alphaMap:this._tex(alpha, true), alphaTest:0.5,
      alphaToCoverage:true, side:THREE.DoubleSide, roughness:0.56, roughnessMap:this.roughTex,
      bumpMap:this._tex(bumpCanvas(), true), bumpScale:0.03,
      metalness:0, clearcoat:0.12, clearcoatRoughness:0.5, envMapIntensity:0.75 });
    this._bumpHoles = state.holes;
    this.mesh = new THREE.Mesh(new THREE.SphereGeometry(0.95, 128, 96), this.mat);
    this.mesh.rotation.y = -Math.PI / 2;   // texture centre u=.5 sits at +x; -90° faces the print to the camera
    this.inner = new THREE.Mesh(new THREE.SphereGeometry(0.92, 64, 48),
      new THREE.MeshStandardMaterial({ color:new THREE.Color(shade(state.color, -0.62)), roughness:0.95, side:THREE.BackSide }));
    this.scene.add(this.mesh); this.scene.add(this.inner);
    this._buildTubes();
    /* soft contact shadow grounds the ball */
    { const sh = document.createElement('canvas'); sh.width = sh.height = 256; const sx = sh.getContext('2d');
      const gg = sx.createRadialGradient(128, 128, 10, 128, 128, 120);
      gg.addColorStop(0, 'rgba(20,24,30,.28)'); gg.addColorStop(0.6, 'rgba(20,24,30,.11)'); gg.addColorStop(1, 'rgba(20,24,30,0)');
      sx.fillStyle = gg; sx.fillRect(0, 0, 256, 256);
      const st = new THREE.CanvasTexture(sh);
      const disc = new THREE.Mesh(new THREE.PlaneGeometry(3.4, 3.4),
        new THREE.MeshBasicMaterial({ map:st, transparent:true, depthWrite:false }));
      disc.rotation.x = -Math.PI/2; disc.position.y = -1.28; this.scene.add(disc); }
    this._loop = this._loop.bind(this); this._loop();
    addEventListener('resize', () => this._resize());
  }
  _tex(cv, linear){ const t = new this.THREE.CanvasTexture(cv); if (!linear) t.colorSpace = this.THREE.SRGBColorSpace; t.anisotropy = 8; return t; }
  /* real moulded holes: one instanced LATHE per hole — a flange tucked under the shell,
   * a quarter-round lip curving from the surface into the bore (NO sharp wall edge), then
   * the straight wall down into the cavity. Oriented along the hole normal in the mesh's
   * local frame (matches SphereGeometry's UV mapping: d = (-cos·cos, sin lat, sin·cos)).
   * Profile y-values are absolute radial distances, so instances need rotation only. */
  _buildTubes(){
    const THREE = this.THREE;
    if (this.tubes){ this.mesh.remove(this.tubes); this.tubes.geometry.dispose(); }
    const pts = holePoints(state.holes), R0 = 0.95, a = HOLE_ANG();
    const rimR = R0 * Math.sin(a), rq = 0.016, boreR = rimR - rq;                        // small roundover = thin, crisp edge
    const ySurf = R0 * Math.cos(a) - 0.0015, tuckR = rimR * 1.12;
    const prof = [new THREE.Vector2(tuckR, Math.sqrt(R0*R0 - tuckR*tuckR) - 0.0025)];    // hidden under the opaque shell
    for (let i = 0; i <= 10; i++){ const t = (i/10) * Math.PI/2;                          // true quarter-round: tangent flat at the
      prof.push(new THREE.Vector2(rimR - rq*Math.sin(t), ySurf - rq*(1 - Math.cos(t)))); }// surface, vertical into the bore — no tier
    prof.push(new THREE.Vector2(boreR, 0.905));                                           // short wall into the cavity (thin shell)
    this.tubeMat = this.tubeMat || new THREE.MeshStandardMaterial({
      color: new THREE.Color(shade(state.color, -0.32)), roughness: 0.8, metalness: 0, side: THREE.DoubleSide });
    const geo = new THREE.LatheGeometry(prof, 40);
    const inst = new THREE.InstancedMesh(geo, this.tubeMat, pts.length);
    const up = new THREE.Vector3(0, 1, 0), qt = new THREE.Quaternion(), m = new THREE.Matrix4(),
          d = new THREE.Vector3(), p = new THREE.Vector3(0, 0, 0), s = new THREE.Vector3(1, 1, 1);
    pts.forEach((h, i) => {
      d.set(-Math.cos(h.lon) * Math.cos(h.lat), Math.sin(h.lat), Math.sin(h.lon) * Math.cos(h.lat));
      qt.setFromUnitVectors(up, d); m.compose(p, qt, s); inst.setMatrixAt(i, m); });
    this.mesh.add(inst); this.tubes = inst; this._tubeHoles = state.holes;
  }
  refresh(){ const { map, alpha } = ballCanvases();
    const o1 = this.mat.map, o2 = this.mat.alphaMap;
    this.mat.map = this._tex(map); this.mat.alphaMap = this._tex(alpha, true);
    if (this._bumpHoles !== state.holes){ const ob = this.mat.bumpMap;
      this.mat.bumpMap = this._tex(bumpCanvas(), true); this._bumpHoles = state.holes; if (ob) ob.dispose(); }
    this.mat.needsUpdate = true;
    if (this._tubeHoles !== state.holes) this._buildTubes();
    this.tubeMat.color.set(shade(state.color, -0.32));
    this.inner.material.color.set(shade(state.color, -0.62));
    if (o1) o1.dispose(); if (o2) o2.dispose(); }
  frontView(){ this.mesh.rotation.y = -Math.PI / 2; this.camera.position.set(0, 0, 5.4); }
  snapshot(){ this.renderer.render(this.scene, this.camera); return this.canvas.toDataURL('image/jpeg', 0.88); }
  _resize(){ const W = this.host.clientWidth, H = this.host.clientHeight; if (!W || !H) return;
    this.camera.aspect = W/H; this.camera.updateProjectionMatrix(); this.renderer.setSize(W, H); }
  _loop(){ requestAnimationFrame(this._loop); this.controls.update(); this.renderer.render(this.scene, this.camera); }
}

/* ---------------- 2D placement view: drag the logo & text on the ball face ---------------- */
class Placement2D {
  constructor(host){
    this.host = host;
    this.canvas = document.createElement('canvas'); this.canvas.className = 'cpb__place';
    this.canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:none;touch-action:none;cursor:default;';
    host.appendChild(this.canvas);
    this.drag = null; this.hover = null;
    this.canvas.addEventListener('pointerdown', e => this._down(e));
    this.canvas.addEventListener('pointermove', e => this._move(e));
    this.canvas.addEventListener('pointerup',   () => { this.drag = null; this.canvas.style.cursor = 'grab'; save(); });
    this.canvas.addEventListener('pointercancel', () => { this.drag = null; });
    addEventListener('resize', () => this.draw());
  }
  _geom(){ const W = this.host.clientWidth, H = this.host.clientHeight;
    return { W, H, cx: W/2, cy: H/2, R: Math.min(W, H) * 0.38 }; }
  _boxes(g){ const b = {}, R = this._R(g);
    if (state.logo){ const im = state.logo, m = R*0.62*state.logoScale;
      const s = Math.min(m/im.naturalWidth, m/im.naturalHeight), p = this._proj(state.logoX, state.logoY, g);
      b.logo = { x: p.x, y: p.y, w: im.naturalWidth*s, h: im.naturalHeight*s, rot: state.logoRot }; }
    if (state.text){ const ctx = this.canvas.getContext('2d'); ctx.font = fontCSS(state.textFont, Math.round(R*0.155*state.textSize));
      const p = this._proj(state.textX, state.textY, g);
      b.text = { x: p.x, y: p.y, w: Math.max(40, ctx.measureText(state.text).width), h: Math.max(24, R*0.18*state.textSize), rot: state.textRot }; }
    return b; }
  _hit(e){ const r = this.canvas.getBoundingClientRect(), px = e.clientX - r.left, py = e.clientY - r.top;
    const g = this._geom(), b = this._boxes(g);
    for (const k of ['logo','text']){ const o = b[k]; if (!o) continue;
      const rr = -(o.rot || 0) * Math.PI / 180, dx = px - o.x, dy = py - o.y;    // test in the item's rotated frame
      const lx = dx*Math.cos(rr) - dy*Math.sin(rr), ly = dx*Math.sin(rr) + dy*Math.cos(rr);
      if (Math.abs(lx) <= o.w/2 + 12 && Math.abs(ly) <= o.h/2 + 12) return { k, px, py, g }; }
    return null; }
  _down(e){ const h = this._hit(e); if (!h) return;
    try { this.canvas.setPointerCapture(e.pointerId); } catch(_){}
    this.canvas.style.cursor = 'grabbing';
    const cur = h.k === 'logo' ? this._proj(state.logoX, state.logoY, h.g) : this._proj(state.textX, state.textY, h.g);
    this.drag = { k: h.k, ox: h.px - cur.x, oy: h.py - cur.y, g: h.g }; }
  _move(e){
    if (!this.drag){ const h = this._hit(e); const nh = h && h.k;
      if (nh !== this.hover){ this.hover = nh; this.canvas.style.cursor = nh ? 'grab' : 'default'; this.draw(); } return; }
    e.preventDefault();
    const d = this.drag, r = this.canvas.getBoundingClientRect();
    const { px, py } = this._unproj((e.clientX - r.left) - d.ox + d.g.cx - d.g.cx, (e.clientY - r.top) - d.oy + 0, d.g);
    /* ox/oy hold the grab offset from the item centre — subtract it, then unproject the centre point */
    if (d.k === 'logo'){ state.logoX = px; state.logoY = py; } else { state.textX = px; state.textY = py; }
    this.draw(); throttled3D(); }
  /* project ball-radius units → screen px (front-locked view; R from the camera maths: r/(2z·tan(fov/2))·H) */
  _R(g){ return 0.3283 * g.H; }
  _proj(px, py, g){ const R = this._R(g), lon = px*0.8, lat = -py*0.8;
    return { x: g.cx + R*Math.cos(lat)*Math.sin(lon), y: g.cy - R*Math.sin(lat) }; }
  _unproj(xp, yp, g){ const R = this._R(g);
    const sinLat = Math.max(-0.95, Math.min(0.95, (g.cy - yp) / R)), lat = Math.asin(sinLat);
    const sinLon = Math.max(-0.95, Math.min(0.95, (xp - g.cx) / (R*Math.cos(lat)))), lon = Math.asin(sinLon);
    return { px: Math.max(-0.72, Math.min(0.72, lon/0.8)), py: Math.max(-0.72, Math.min(0.72, -lat/0.8)) }; }
  draw(){
    const g = this._geom(); if (!g.W) return;
    const dpr = Math.min(devicePixelRatio, 2);
    if (this.canvas.width !== Math.round(g.W*dpr)){ this.canvas.width = Math.round(g.W*dpr); this.canvas.height = Math.round(g.H*dpr); }
    const x = this.canvas.getContext('2d'); x.setTransform(dpr, 0, 0, dpr, 0, 0); x.clearRect(0, 0, g.W, g.H);
    if (ball && ball.canvas){
      /* the placement backdrop IS the real 3D ball, rendered front-locked for one frame */
      const rotY = ball.mesh.rotation.y, cp = ball.camera.position, old = { x: cp.x, y: cp.y, z: cp.z };
      ball.mesh.rotation.y = -Math.PI/2; cp.set(0, 0, 5.4); ball.camera.lookAt(0, 0, 0);
      ball.renderer.render(ball.scene, ball.camera);
      x.drawImage(ball.canvas, 0, 0, g.W, g.H);
      ball.mesh.rotation.y = rotY; cp.set(old.x, old.y, old.z); ball.camera.lookAt(0, 0, 0);
    } else {
      /* no-WebGL fallback: hand-drawn ball with the same structured hole rows */
      const { cx, cy } = g, R = this._R(g);
      x.fillStyle = state.color; x.beginPath(); x.arc(cx, cy, R, 0, Math.PI*2); x.fill();
      const gr = x.createRadialGradient(cx - R*0.35, cy - R*0.45, R*0.1, cx, cy, R*1.02);
      gr.addColorStop(0, 'rgba(255,255,255,.7)'); gr.addColorStop(0.4, 'rgba(255,255,255,.08)'); gr.addColorStop(1, 'rgba(0,0,0,.25)');
      x.fillStyle = gr; x.beginPath(); x.arc(cx, cy, R, 0, Math.PI*2); x.fill();
      const rim = shade(state.color, -0.32), angR = HOLE_ANG();
      for (const h of holePoints(state.holes)){
        const X = Math.cos(h.lat)*Math.sin(h.lon), Y = Math.sin(h.lat), Z = Math.cos(h.lat)*Math.cos(h.lon);
        if (Z < 0.2) continue;
        const px = cx + X*R*0.99, py = cy - Y*R*0.99, hr = R*Math.sin(angR)*1.6;
        x.save(); x.translate(px, py);
        x.fillStyle = 'rgba(12,13,16,.8)'; x.beginPath(); x.ellipse(0, 0, hr*Z, hr, 0, 0, Math.PI*2); x.fill();
        x.strokeStyle = rim; x.lineWidth = hr*0.18; x.beginPath(); x.ellipse(0, 0, hr*Z*1.08, hr*1.08, 0, 0, Math.PI*2); x.stroke();
        x.restore(); }
      if (state.logo){ const o = this._boxes(g).logo; if (o){
        x.save(); x.translate(o.x, o.y); x.rotate((o.rot||0)*Math.PI/180);
        x.scale(state.logoFlipX ? -1 : 1, state.logoFlipY ? -1 : 1);
        x.drawImage(state.logo, -o.w/2, -o.h/2, o.w, o.h); x.restore(); } }
      if (state.text){ const o = this._boxes(g).text; if (o){
        x.save(); x.translate(o.x, o.y); x.rotate((o.rot||0)*Math.PI/180); x.fillStyle = state.textColor;
        x.font = fontCSS(state.textFont, Math.round(this._R(g)*0.155*state.textSize)); x.textAlign = 'center'; x.textBaseline = 'middle';
        x.fillText(state.text, 0, 0); x.restore(); } }
    }
    /* drag affordances over the render (drawn in each item's rotated frame) */
    const b = this._boxes(g);
    x.setLineDash([6, 5]); x.lineWidth = 1.6;
    for (const k of ['logo','text']){ const o = b[k]; if (!o) continue;
      const on = this.hover === k || (this.drag && this.drag.k === k);
      x.strokeStyle = on ? '#f05828' : 'rgba(240,88,40,.55)';
      x.save(); x.translate(o.x, o.y); x.rotate((o.rot||0)*Math.PI/180);
      x.strokeRect(-o.w/2 - 8, -o.h/2 - 8, o.w + 16, o.h + 16); x.restore(); }
    x.setLineDash([]);
    const msg = state.logo || state.text ? 'Drag your logo & text to place them' : 'Upload a logo or add text, then drag to place';
    x.font = '600 12.5px sans-serif'; const tw = x.measureText(msg).width;
    x.fillStyle = 'rgba(255,255,255,.85)'; x.beginPath();
    x.roundRect ? x.roundRect(g.cx - tw/2 - 12, g.H - 34, tw + 24, 24, 12) : x.rect(g.cx - tw/2 - 12, g.H - 34, tw + 24, 24); x.fill();
    x.fillStyle = '#4b5563'; x.textAlign = 'center'; x.textBaseline = 'middle'; x.fillText(msg, g.cx, g.H - 22);
  }
}
let _t3 = 0;
function throttled3D(){ const now = performance.now();
  if (now - _t3 > 90){ _t3 = now; if (ball && ball.refresh) ball.refresh(); }
  else { clearTimeout(throttled3D._t); throttled3D._t = setTimeout(() => { _t3 = performance.now(); if (ball && ball.refresh) ball.refresh(); }, 140); } }

/* ---------------- summary / persistence / quote ---------------- */
function summaryText(){ const bits = [`${colorName(state.color)} (${state.color})`, state.holes === 40 ? 'Outdoor · 40 holes' : 'Indoor · 26 holes'];
  if (state.logoSrc) bits.push('Logo uploaded'); if (state.text) bits.push(`Text “${state.text}” (${fontOf(state.textFont)[1]})`);
  return bits.join(' · '); }
function updateSummary(){ const s = el('cpb-summary'); if (s) s.textContent = 'Your custom pickleball — ' + summaryText(); }
function serialize(){ return { v:3, color:state.color, holes:state.holes, logoSrc:state.logoSrc, logoScale:state.logoScale,
  removeWhite:state.removeWhite, logoX:state.logoX, logoY:state.logoY, logoRot:state.logoRot,
  logoFlipX:state.logoFlipX, logoFlipY:state.logoFlipY, text:state.text, textFont:state.textFont, textColor:state.textColor,
  textSize:state.textSize, textX:state.textX, textY:state.textY, textRot:state.textRot, notes:state.notes }; }
let saveT = null;
function save(){ clearTimeout(saveT); saveT = setTimeout(() => { try { localStorage.setItem(LS_KEY, JSON.stringify(serialize())); } catch(_){} }, 400); }
function refresh(){ if (ball && ball.refresh) ball.refresh(); if (place) place.draw(); updateSummary(); save(); }

function buildProof(){
  const shot = ball && ball.snapshot ? ball.snapshot() : null;
  const c = document.createElement('canvas'); c.width = 900; c.height = 1020; const x = c.getContext('2d');
  x.fillStyle = '#fff'; x.fillRect(0, 0, 900, 1020);
  x.fillStyle = '#1b1e24'; x.font = '800 34px sans-serif'; x.textAlign = 'center';
  x.fillText('Custom Pickleball — design proof', 450, 56);
  return new Promise(res => { const done = () => { x.font = '600 22px sans-serif'; x.textAlign = 'left'; x.fillStyle = '#1b1e24';
      const lines = [`Colour: ${colorName(state.color)}  ${state.color}`, `Type: ${state.holes === 40 ? 'Outdoor (40 holes)' : 'Indoor (26 holes)'}`,
        `Logo: ${state.logoSrc ? 'uploaded ✓' + (state.logoRot ? ` · ${state.logoRot}°` : '') + (state.logoFlipX || state.logoFlipY ? ' · flipped' : '') : '—'}`,
        `Text: ${state.text ? `“${state.text}”  (${fontOf(state.textFont)[1]}, ${state.textColor}${state.textRot ? `, ${state.textRot}°` : ''})` : '—'}`,
        `Notes: ${state.notes || '—'}`];
      let y = 840; for (const l of lines){ x.fillText(l.slice(0, 70), 60, y); y += 34; }
      res(c.toDataURL('image/jpeg', 0.85)); };
    if (!shot) return done();
    const im = new Image(); im.onload = () => { const s = Math.min(760/im.width, 700/im.height);
      x.drawImage(im, 450 - im.width*s/2, 90, im.width*s, im.height*s); done(); }; im.onerror = done; im.src = shot; });
}

async function requestQuote(btn){
  btn.disabled = true; const old = btn.textContent; btn.textContent = 'Preparing design…';
  try {
    if (ball && ball.frontView) ball.frontView();
    const pack = { v:1, product:'pickleball', when:Date.now(), summary:'Custom Pickleball | ' + summaryText() + (state.notes ? ' | Notes: ' + state.notes : ''),
      json: JSON.stringify(serialize()), proof: await buildProof() };
    localStorage.setItem('cpd_quote_v1', JSON.stringify(pack));
  } catch(_){}
  btn.textContent = old; btn.disabled = false;
  location.href = CFG.consultUrl || '/pages/custom-design';
}

/* ---------------- UI ---------------- */
function buildColors(){ const host = el('cpb-colors'); host.innerHTML = '';
  BALL_COLORS.forEach(([name, hex]) => { const b = document.createElement('button'); b.type = 'button';
    b.className = 'cpb__color' + (state.color.toLowerCase() === hex.toLowerCase() ? ' is-active' : '');
    b.style.background = hex; b.title = name; b.setAttribute('aria-label', name);
    b.onclick = () => { state.color = hex; buildColors(); const cc = el('cpb-customcolor'); if (cc) cc.value = hex; refresh(); };
    host.appendChild(b); }); }
function syncType(){ const o = el('cpb-outdoor'), i = el('cpb-indoor');
  if (o) o.classList.toggle('is-active', state.holes === 40);
  if (i) i.classList.toggle('is-active', state.holes === 26); }
function setView(v){ state.view = v;
  document.querySelectorAll('[data-cpbview]').forEach(b => b.classList.toggle('is-active', b.dataset.cpbview === v));
  if (place) place.canvas.style.display = v === '2d' ? 'block' : 'none';
  if (ball && ball.canvas) ball.canvas.style.display = v === '2d' ? 'none' : 'block';
  const badge = document.querySelector('.cpb .cpd__preview-badge');
  if (badge) badge.textContent = v === '2d' ? 'Placement — drag to arrange' : 'Live 3D preview';
  if (v === '2d' && place) place.draw(); }
function syncArtControls(){
  const set = (id, v) => { const n = el(id); if (n) n.value = v; };
  set('cpb-font', state.textFont); set('cpb-textsize', state.textSize); set('cpb-textrot', state.textRot);
  set('cpb-logosize', state.logoScale); set('cpb-logorot', state.logoRot);
  const put = (id, v) => { const n = el(id); if (n) n.textContent = v; };
  put('cpb-textsize-val', Math.round(state.textSize*100) + '%'); put('cpb-textrot-val', state.textRot + '°');
  put('cpb-logosize-val', Math.round(state.logoScale*100) + '%'); put('cpb-logorot-val', state.logoRot + '°');
  const flag = (id, on) => { const n = el(id); if (n) n.classList.toggle('is-active', !!on); };
  flag('cpb-flipx', state.logoFlipX); flag('cpb-flipy', state.logoFlipY);
}
async function setLogo(src){ state.logoSrc = src || '';
  state.logo = src ? await loadImg(state.removeWhite ? removeWhiteBg(await loadImg(src)) : src) : null;
  const row = el('cpb-logorow'); row.style.display = src ? 'flex' : 'none';
  if (src) el('cpb-logothumb').src = src;
  refresh(); }

async function init(){
  buildColors(); syncType(); updateSummary();
  el('cpb-customcolor').oninput = e => { state.color = e.target.value; buildColors(); refresh(); };
  el('cpb-outdoor').onclick = () => { state.holes = 40; syncType(); refresh(); };
  el('cpb-indoor').onclick = () => { state.holes = 26; syncType(); refresh(); };
  el('cpb-logo-input').onchange = async e => { const f = e.target.files[0]; if (!f) return;
    try { const raw = await loadImg(await fileToDataURL(f)); await setLogo(downscale(raw, 700)); setView('2d'); } catch(_){} e.target.value = ''; };
  el('cpb-logoclear').onclick = () => setLogo('');
  el('cpb-removewhite').onchange = e => { state.removeWhite = e.target.checked; if (state.logoSrc) setLogo(state.logoSrc); };
  el('cpb-logosize').oninput = e => { state.logoScale = parseFloat(e.target.value);
    el('cpb-logosize-val').textContent = Math.round(state.logoScale*100) + '%'; refresh(); };
  el('cpb-logorot').oninput = e => { state.logoRot = parseInt(e.target.value, 10) || 0;
    el('cpb-logorot-val').textContent = state.logoRot + '°'; refresh(); };
  el('cpb-flipx').onclick = function(){ state.logoFlipX = !state.logoFlipX; this.classList.toggle('is-active', state.logoFlipX); refresh(); };
  el('cpb-flipy').onclick = function(){ state.logoFlipY = !state.logoFlipY; this.classList.toggle('is-active', state.logoFlipY); refresh(); };
  el('cpb-logoreset').onclick = () => { state.logoRot = 0; state.logoFlipX = state.logoFlipY = false; state.logoScale = 1;
    state.logoX = 0; state.logoY = -0.22; syncArtControls(); refresh(); };
  { const fsel = el('cpb-font');
    FONTS.forEach(([k, label]) => { const o = document.createElement('option'); o.value = k; o.textContent = label; fsel.appendChild(o); });
    fsel.onchange = e => { state.textFont = e.target.value; refresh(); }; }
  el('cpb-text').oninput = e => { state.text = e.target.value; refresh(); };
  el('cpb-textcolor').oninput = e => { state.textColor = e.target.value; refresh(); };
  el('cpb-textsize').oninput = e => { state.textSize = parseFloat(e.target.value);
    el('cpb-textsize-val').textContent = Math.round(state.textSize*100) + '%'; refresh(); };
  el('cpb-textrot').oninput = e => { state.textRot = parseInt(e.target.value, 10) || 0;
    el('cpb-textrot-val').textContent = state.textRot + '°'; refresh(); };
  el('cpb-notes').oninput = e => { state.notes = e.target.value; save(); };
  el('cpb-quote').onclick = function(){ requestQuote(this); };
  el('cpb-download').onclick = async () => { const a = document.createElement('a');
    a.href = await buildProof(); a.download = 'canvas-custom-pickleball.jpg'; a.click(); };
  document.querySelectorAll('[data-cpbview]').forEach(b => { b.onclick = () => setView(b.dataset.cpbview); });

  try { const saved = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
    if (saved && saved.v >= 1){ Object.assign(state, { color:saved.color || state.color, holes:saved.holes || 40,
        logoScale:saved.logoScale || 1, removeWhite:!!saved.removeWhite, text:saved.text || '', textColor:saved.textColor || '#111111',
        textSize:saved.textSize || 1, notes:saved.notes || '',
        logoX:saved.logoX ?? 0, logoY:saved.logoY ?? -0.22, textX:saved.textX ?? 0, textY:saved.textY ?? 0.60,
        textFont:saved.textFont || 'sans', textRot:saved.textRot || 0, logoRot:saved.logoRot || 0,
        logoFlipX:!!saved.logoFlipX, logoFlipY:!!saved.logoFlipY });
      buildColors(); syncType();
      el('cpb-text').value = state.text; el('cpb-textcolor').value = state.textColor;
      el('cpb-removewhite').checked = state.removeWhite; el('cpb-notes').value = state.notes;
      el('cpb-customcolor').value = state.color;
      syncArtControls();
      if (saved.logoSrc) await setLogo(saved.logoSrc);
      updateSummary(); } } catch(_){}

  const host = el('cpb-canvas-host');
  try {
    const THREE = await import(CFG.threeUrl);
    const { OrbitControls } = await import(CFG.orbitUrl);
    let RoomEnvironment = null; try { ({ RoomEnvironment } = await import(CFG.roomUrl)); } catch(e){}
    ball = new Ball3D(THREE, OrbitControls, host, RoomEnvironment);
  } catch(e){ console.warn('[pickleball-designer] 3D unavailable', e); ball = null; }
  place = new Placement2D(host);
  if (!ball) setView('2d'); else setView('3d');
  const l = el('cpb-loading'); if (l) l.style.display = 'none';
  window.CPBDBG = { state, get ball(){ return ball; }, get place(){ return place; }, refresh, setView, proof:buildProof };
}
init();
